import type {
  UploadMethod,
  UploadMethodConfig,
  UploadMethodGroupConfig,
} from "@/types/knowledge/knowledge-acquisition";

export type { UploadMethod, UploadMethodConfig } from "@/types/knowledge/knowledge-acquisition";

export const UPLOAD_METHODS: UploadMethodConfig[] = [
  {
    id: "bulk",
    label: "Bulk Upload",
    genieIcon: "files",
    description: "Multiple files at once",
    compactMode: false,
  },
  {
    id: "pdf",
    label: "PDF File",
    genieIcon: "document",
    description: "Upload PDF documents",
    compactMode: true,
  },
  {
    id: "url",
    label: "PDF URL",
    genieIcon: "document",
    description: "Extract from PDF link",
    compactMode: true,
  },
  {
    id: "youtube",
    label: "YouTube",
    genieIcon: "clapperboard",
    description: "Extract video transcript",
    compactMode: true,
  },
  {
    id: "website",
    label: "Website",
    genieIcon: "globe",
    description: "Scrape web content",
    compactMode: true,
  },
  {
    id: "website-brightdata",
    label: "Website (Alternate)",
    genieIcon: "globe",
    description: "Scrape web content, alternate engine",
    compactMode: true,
  },
  {
    id: "website-crawl",
    label: "Web Scraping (Alternate)",
    genieIcon: "globe",
    description: "Multi-page site crawler, alternate engine",
    compactMode: true,
  },
  {
    id: "audio",
    label: "Audio",
    genieIcon: "microphone",
    description: "Transcribe audio files",
    compactMode: true,
  },
  {
    id: "document",
    label: "Document",
    genieIcon: "document",
    description: "TXT, CSV, DOC, DOCX",
    compactMode: true,
  },
  {
    id: "video",
    label: "Video",
    genieIcon: "clapperboard",
    description: "Extract video transcript",
    compactMode: true,
  },
  {
    id: "image",
    label: "Image",
    genieIcon: "palette",
    description: "Extract text from images",
    compactMode: true,
  },
  {
    id: "notes",
    label: "Notes",
    genieIcon: "chat",
    description: "Create text notes",
    compactMode: true,
  },
  {
    id: "web-scraping",
    label: "Web Scraping",
    genieIcon: "globe",
    description: "Multi-page site crawler",
    compactMode: true,
  },
  {
    id: "quick-research",
    label: "Quick Research",
    genieIcon: "brain",
    description: "Fast research queries",
    compactMode: true,
  },
  {
    id: "deep-research",
    label: "Deep Research",
    genieIcon: "brain",
    description: "In-depth analysis",
    compactMode: true,
  },
  {
    id: "cloud-storage",
    label: "Cloud Storage",
    genieIcon: "dashboard",
    description: "Google Drive & OneDrive",
    compactMode: true,
  },
  {
    id: "custom-integration",
    label: "Custom Integration",
    genieIcon: "trending-up",
    description: "Book a consultation",
    compactMode: true,
  },
];

export const UPLOAD_METHOD_GROUPS: UploadMethodGroupConfig[] = [
  {
    id: "files-cloud",
    label: "Files & cloud",
    description: "Bring in material you already have",
    methodIds: ["bulk", "pdf", "document", "image", "cloud-storage"],
  },
  {
    id: "web-media",
    label: "Web & media",
    description: "Capture links, sites, audio, and video",
    methodIds: ["url", "website", "website-brightdata", "web-scraping", "website-crawl", "youtube", "audio", "video"],
  },
  {
    id: "write-research",
    label: "Write & research",
    description: "Create new knowledge from what your team knows",
    methodIds: ["notes", "quick-research", "deep-research", "custom-integration"],
  },
];

export const isUploadMethod = (value: string | null): value is UploadMethod =>
  UPLOAD_METHODS.some((method) => method.id === value && method.id !== "custom-integration");

export const getUploadMethodGroup = (method: UploadMethod) =>
  UPLOAD_METHOD_GROUPS.find((group) => group.methodIds.includes(method));

export const getMethodsForGroup = (group: UploadMethodGroupConfig) =>
  group.methodIds
    .map((methodId) => UPLOAD_METHODS.find((method) => method.id === methodId))
    .filter((method): method is UploadMethodConfig => Boolean(method));

export const getCompactMethods = () => UPLOAD_METHODS.filter((m) => m.compactMode);
export const getAllMethods = () => UPLOAD_METHODS;
